import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ProjectList from "~/components/Project/projectList";
import SectionHeader from "~/components/Project/sectionHeaderProjectList";
import TutorialModal from "~/components/Tutorial/TutorialModal";
import Button from "~/components/ui/button";
import Modal from "~/components/ui/modal";
import { TOAST_MESSAGES } from "~/constants/messages";
import { SAMPLE_PROJECT_ID, sampleProjectsEntry } from "~/constants/sampleProject";
import ProjectModalContent from "~/features/projectTop/projectModalContent";
import { ProjectApi } from "~/functions/api/project";
import { useAuthGuard } from "~/hooks/useAuthGuard";
import reqxignAppGenerationRelease from "~/images/reqxign_app_generation_release.png";
import type { CreateProject, Project, Projects } from "~/interface/project";
import { setCurrentProject, setTourCompleted } from "~/slices/auth";
import type { AppDispatch, RootState } from "~/store";
import { dateToISOString } from "~/utils/customDate";
import { startTutorialTour, TUTORIAL_EVENTS } from "~/utils/tutorialEvents";
import { useNativePushNotification } from "~/utils/useNativePushNotification";
import { useOnceShowPopup } from "~/utils/useOnceShowPopup";

import styles from "./index.module.scss";

const TUTORIAL_COMPLETED_KEY = "reqxign_tutorial_completed";
const TUTORIAL_HIDDEN_SAMPLE_KEY = "reqxign_tutorial_hidden_sample";

const Home = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLock, setIsLock] = useState(false);
  const [projects, setProjects] = useState<Projects[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [dateValidationError, setDateValidationError] = useState<string | null>(null);
  const [isSampleHidden, setIsSampleHidden] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(TUTORIAL_HIDDEN_SAMPLE_KEY) === "true";
  });
  const dispatch = useDispatch<AppDispatch>();
  const { status, tour_completed } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const { permission, requestPermission } = useNativePushNotification();
  const [isTutorialModalOpen, setIsTutorialModalOpen] = useState(false);
  const [isTutorialCompleteModalOpen, setIsTutorialCompleteModalOpen] = useState(false);
  const { shouldShow: shouldShowOnceShowPopup, markAsShown: markOnceShowPopupAsShown } =
    useOnceShowPopup();
  const [isOnceShowPopupOpen, setIsOnceShowPopupOpen] = useState(false);
  const [isOnceShowPopupDoNotShowAgain, setIsOnceShowPopupDoNotShowAgain] = useState(false);

  useEffect(() => {
    if (shouldShowOnceShowPopup) {
      setIsOnceShowPopupOpen(true);
    }
  }, [shouldShowOnceShowPopup]);

  const handleCloseOnceShowPopup = () => {
    setIsOnceShowPopupOpen(false);
    if (isOnceShowPopupDoNotShowAgain) {
      markOnceShowPopupAsShown();
    }
  };

  // 認証チェック用のフックを使用
  const { ensure, ids } = useAuthGuard();

  const [newProject, setNewProject] = useState<CreateProject>({
    name: "",
    description: undefined,
    start_date: undefined,
    end_date: undefined,
    client: {
      name: undefined,
      url: undefined,
    },
  });

  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  // モーダルを閉じる関数
  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Fetch projects data
  const fetchProjects = useCallback(async () => {
    // 認証チェック: currentUserが無効な場合は何もしない
    if (!ids) {
      return;
    }

    setIsLoading(true);
    const projectApi = new ProjectApi();
    const response = await projectApi.getAllProjects(ids.organizationId);
    if (response !== null) {
      const projects = response.projects;
      const totalCount = response.totalCount;
      setProjects(projects);
      setTotalCount(totalCount);
    } else {
      toast.error(TOAST_MESSAGES.PROJECT.FETCH_PROJECTS_FAILED);
    }
    setIsLoading(false);
  }, [ids]);

  const fetchCreateProject = useCallback(
    async (createProject: CreateProject) => {
      // 認証チェック: currentUserが無効な場合はnullを返す
      if (!ids) {
        return null;
      }

      const projectData: CreateProject = {
        name: createProject.name,
        description: createProject.description,
        start_date: createProject.start_date,
        end_date: createProject.end_date,
        client: {
          name: createProject.client.name,
          url: createProject.client.url,
        },
      };

      const projectApi = new ProjectApi();
      const response = await projectApi.create(ids.organizationId, projectData);

      if (response !== null) {
        toast.success(TOAST_MESSAGES.PROJECT.CREATE_SUCCESS);
        return response;
      } else {
        toast.error(TOAST_MESSAGES.PROJECT.CREATE_FAILED);
        return null;
      }
    },
    [ids],
  );

  const handleCreate = useCallback(async () => {
    // 認証チェック: currentUserが無効な場合はリダイレクト
    const authIds = ensure();
    if (!authIds) {
      return;
    }

    setIsLock(true);
    try {
      const response = await fetchCreateProject(newProject);
      if (response === null) {
        return;
      }

      const project: Project = response;
      dispatch(setCurrentProject(project));

      await fetchProjects();

      const orgId = authIds.organizationId;
      const projId = project.id;
      navigate(`/${orgId}/project/${projId}/dashboard?new=true`);

      closeModal();
    } finally {
      setIsLock(false);
    }
  }, [ensure, newProject, dispatch, fetchCreateProject, fetchProjects, navigate, closeModal]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "client.name") {
      setNewProject((prevProject) => ({
        ...prevProject,
        client: {
          ...prevProject.client,
          name: value,
        },
      }));
    } else if (name === "client.url") {
      setNewProject((prevProject) => ({
        ...prevProject,
        client: {
          ...prevProject.client,
          url: value,
        },
      }));
    } else if (name === "start_date" || name === "end_date") {
      // 日付の型チェックを強化
      if (value) {
        const dateValue = new Date(value);
        if (Number.isNaN(dateValue.getTime())) {
          toast.warning(TOAST_MESSAGES.PROJECT.INVALID_DATE_FORMAT);
          return;
        }
        setNewProject((prevProject) => ({
          ...prevProject,
          [name]: dateToISOString(dateValue),
        }));
      } else {
        setNewProject((prevProject) => ({
          ...prevProject,
          [name]: undefined,
        }));
      }
    } else {
      setNewProject((prevProject) => ({
        ...prevProject,
        [name]: value !== "" ? value : undefined,
      }));
    }
  }, []);

  const modalContent = useMemo(
    () => (
      <ProjectModalContent
        projectData={newProject}
        handleInputChange={handleInputChange}
        handleCreate={handleCreate}
        closeModal={closeModal}
        isFormValid={isFormValid}
        isLock={isLock}
        startDateString={newProject.start_date ? newProject.start_date.split("T")[0] : ""}
        endDateString={newProject.end_date ? newProject.end_date.split("T")[0] : ""}
        buttonLabel={!isLock ? "作成" : "作成中"}
        isEditMode={false}
        dateValidationError={dateValidationError}
      />
    ),
    [
      newProject,
      isFormValid,
      isLock,
      handleInputChange,
      handleCreate,
      closeModal,
      dateValidationError,
    ],
  );

  const displayProjects = useMemo(() => {
    if (isSampleHidden) {
      return projects;
    }
    // undefinedチェックを追加
    if (!Array.isArray(projects) || projects.length === 0) {
      return [
        {
          ...sampleProjectsEntry,
          project: {
            ...sampleProjectsEntry.project,
            name: "サンプルプロジェクト (チュートリアル)",
          }, // Added name for clarity in tests
          users: [...sampleProjectsEntry.users],
        },
      ];
    }
    const hasSample = projects.some((entry) => entry?.project?.id === SAMPLE_PROJECT_ID);
    if (hasSample) {
      return projects;
    }
    return [
      {
        ...sampleProjectsEntry,
        project: { ...sampleProjectsEntry.project },
        users: [...sampleProjectsEntry.users],
      },
      ...projects,
    ];
  }, [projects, isSampleHidden]);

  useEffect(() => {
    dispatch(setCurrentProject(null));
  }, [dispatch]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const completionValue = localStorage.getItem(TUTORIAL_COMPLETED_KEY);
    if (completionValue === "true" || completionValue === "skipped") {
      dispatch(setTourCompleted(true));
    }
  }, [dispatch]);

  useEffect(() => {
    // プロジェクト名の長さ制限チェック（100文字）
    const isNameValid = Boolean(
      newProject.name && newProject.name.trim().length > 0 && newProject.name.length <= 100,
    );

    // 日付の妥当性チェック（start_date < end_date）
    let areDatesValid = true;
    let dateError: string | null = null;
    if (newProject.start_date && newProject.end_date) {
      const startDate = new Date(newProject.start_date);
      const endDate = new Date(newProject.end_date);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        areDatesValid = false;
      } else if (startDate > endDate) {
        areDatesValid = false;
        dateError = "終了日が開始日より以前の日付で設定されています。";
      }
    }

    setDateValidationError(dateError);
    const isFormFilled = isNameValid && areDatesValid;
    setIsFormValid(isFormFilled);
  }, [newProject]);

  useEffect(() => {
    if (permission === "default") {
      const askPermission = async () => {
        await requestPermission();
      };
      askPermission();
    }
  }, [permission, requestPermission]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleTutorialStart = () => {
      setIsSampleHidden(false);
      dispatch(setTourCompleted(false));
    };
    window.addEventListener(TUTORIAL_EVENTS.START_TOUR, handleTutorialStart);
    return () => window.removeEventListener(TUTORIAL_EVENTS.START_TOUR, handleTutorialStart);
  }, [dispatch]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (!isLoading && projects.length === 0 && !tour_completed) {
      const completedValue =
        typeof window !== "undefined" ? localStorage.getItem(TUTORIAL_COMPLETED_KEY) : null;
      const legacyDismissed =
        typeof window !== "undefined" ? localStorage.getItem("reqxign_tutorial_dismissed") : null;
      if (!completedValue && !legacyDismissed) {
        setIsTutorialModalOpen(true);
      }
    }
  }, [isLoading, projects.length, tour_completed]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const shouldShowCreatePrompt =
      localStorage.getItem("reqxign_tutorial_show_create_prompt") === "true";
    const shouldOpenCreateModal =
      localStorage.getItem("reqxign_tutorial_open_create_modal") === "true";

    if (shouldOpenCreateModal) {
      localStorage.removeItem("reqxign_tutorial_open_create_modal");
      localStorage.removeItem("reqxign_tutorial_show_create_prompt");
      setIsTutorialCompleteModalOpen(false);
      openModal();
    } else if (shouldShowCreatePrompt) {
      setIsTutorialCompleteModalOpen(true);
    }
  }, [openModal]);

  const handleStartTour = () => {
    setIsTutorialModalOpen(false);
    if (typeof window !== "undefined") {
      localStorage.removeItem(TUTORIAL_HIDDEN_SAMPLE_KEY);
    }
    setIsSampleHidden(false);
    dispatch(setTourCompleted(false));
    startTutorialTour();
  };

  const handleLaterTour = () => {
    setIsTutorialModalOpen(false);
  };

  const handleSkipTour = () => {
    setIsTutorialModalOpen(false);
    if (typeof window !== "undefined") {
      localStorage.setItem("reqxign_tutorial_dismissed", "true");
      localStorage.setItem(TUTORIAL_COMPLETED_KEY, "skipped");
    }
    dispatch(setTourCompleted(true));
  };

  const handleHideSampleProject = () => {
    setIsSampleHidden(true);
    if (typeof window !== "undefined") {
      localStorage.setItem(TUTORIAL_HIDDEN_SAMPLE_KEY, "true");
    }
    toast.info(TOAST_MESSAGES.PROJECT.SAMPLE_HIDDEN);
  };

  return (
    <div data-testid="home-page">
      <div className={styles["title"]}>
        <SectionHeader
          title="プロジェクト"
          buttonLabel="新規作成"
          onButtonClick={openModal}
          buttonStyle="create-project"
          totalProjects={totalCount}
        />
      </div>
      {status === "idle" && (
        <ProjectList
          projects={displayProjects}
          isLoading={isLoading}
          onRefresh={fetchProjects}
          onHideSampleProject={handleHideSampleProject}
          onSelectSampleProject={handleStartTour}
        />
      )}
      <Modal
        label={"プロジェクト新規作成"}
        isOpen={isModalOpen}
        onClose={closeModal}
        content={modalContent}
        className="projectCreateModal"
      />
      <Modal
        label={"チュートリアル完了！"}
        isOpen={isTutorialCompleteModalOpen}
        onClose={() => {
          setIsTutorialCompleteModalOpen(false);
          localStorage.removeItem("reqxign_tutorial_show_create_prompt");
        }}
        content={
          <div className={styles["tutorialCompleteModal"]}>
            <h3>チュートリアルお疲れさまでした！</h3>
            <p>
              さあ、プロジェクトを作成してみましょう！REQXIGNの機能を実際の案件で活用してみてください。
            </p>
            <Button
              label="プロジェクトを作成してみる"
              styleType="primary"
              onClick={() => {
                localStorage.removeItem("reqxign_tutorial_show_create_prompt");
                setIsTutorialCompleteModalOpen(false);
                openModal();
              }}
            />
          </div>
        }
      />
      <Modal
        label={"新機能がリリースされました！🎉"}
        isOpen={isOnceShowPopupOpen}
        onClose={handleCloseOnceShowPopup}
        usePortal
        showCloseButton={false}
        className="releaseAnnouncementModal"
        content={
          <div className={styles["onceShowPopupContent"]}>
            <p className={styles["lead"]}>
              いつもレクシンをご利用いただきありがとうございます。
              <br />
              レクシンは4月14日に皆さまのkintone開発を劇的に進化させる
              <br />
              2つの大きなアップデートを実施しました！
            </p>
            <img
              src={reqxignAppGenerationRelease}
              alt="レクシン新機能リリース"
              className={styles["releaseImage"]}
            />
            <h3 className={styles["sectionHeading"]} data-index="NEW">
              【新機能】議事録からkintoneアプリを自動で生成！
            </h3>
            <p>
              これからのレクシンは設計書を作るだけで終わりではありません。
              生成された「アプリ設計書」等をもとに、kintoneアプリをボタンクリックで自動生成できるようになりました。
            </p>
            <ul>
              <li>複数のアプリも一括生成</li>
              <li>ルックアップや関連レコードなどの複雑な連携も自動設定</li>
              <li>「使いやすさ」を考慮したフィールド配置</li>
            </ul>
            <h3 className={styles["sectionHeading"]} data-index="UPD">
              トップ画面がより分かりやすくリニューアル！
            </h3>
            <p>
              「次に何をすればいいか」がひと目で分かるデザインに刷新しました。
              迷うことなく、最短ルートで理想のシステムへ辿り着けます。
            </p>
            <p className={styles["closing"]}>
              「ドキュメント作成」から「システム完成」まで、レクシンがさらに加速させます。
              <br />
              新しくなったレクシンを、ぜひ体験してください！
            </p>
          </div>
        }
        footer={
          <div className={styles["onceShowPopupFooter"]}>
            <label className={styles["onceShowPopupDoNotShowAgain"]}>
              <input
                type="checkbox"
                checked={isOnceShowPopupDoNotShowAgain}
                onChange={(e) => setIsOnceShowPopupDoNotShowAgain(e.target.checked)}
              />
              今後表示しない
            </label>
            <Button label="閉じる" onClick={handleCloseOnceShowPopup} />
          </div>
        }
      />
      {isTutorialModalOpen && (
        <TutorialModal
          onStart={handleStartTour}
          onLater={handleLaterTour}
          onSkip={handleSkipTour}
        />
      )}
    </div>
  );
};

export default Home;
